import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';
import type { ListResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';
import type { AnalyticalVariableListItem } from './useAnalyticalVariableListQuery';

export interface DeleteAnalyticalVariableMutationInput {
  analyticalVariableId: string;
  /**
   * Project the delete was triggered from, when known. Sent to the server for
   * scope visibility and used to drop the row from that project's artifact
   * list immediately. Omit for the org-wide variable library page.
   */
  projectId?: string;
}

/**
 * Mirrors ``projectPageKeys.artifactsForProject`` BY VALUE — this package
 * cannot import feature-project-page (build cycle); see
 * ``useRenameAvMutation`` for the same bridge.
 */
const projectArtifactsKey = (projectId: string) => ['projectPage', 'artifacts', projectId] as const;

interface ProjectArtifactListEntryShape {
  id: string;
  kind: string;
}

/**
 * OR-6234: delete an analytical variable — ``DELETE /analytical-variables/{id}``.
 *
 * The server owns every check (owner, not in use, flag) and answers 204, or
 * 403 / 404 / 409. On success the variable is gone from every list, so this
 * drops it from each cached variable list and from the project artifact list
 * when a project is known, removes its detail entry, and then invalidates the
 * whole analytical-variable namespace so the authoritative refetch lands.
 * Shared by the project artifact list and the variable library page.
 */
export const useDeleteAnalyticalVariableMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteAnalyticalVariableMutationInput>({
    mutationKey: [ANALYTICAL_VARIABLE_WRITE_MUTATION_KEY],
    mutationFn: ({ analyticalVariableId, projectId }) => {
      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      const qs = params.toString() ? `?${params.toString()}` : '';
      return authFetch<void>(
        `/analytical-variables/${encodeURIComponent(analyticalVariableId)}${qs}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: (_data, { analyticalVariableId, projectId }) => {
      queryClient.setQueriesData<ListResponse<AnalyticalVariableListItem>>(
        { queryKey: avKeys.library() },
        (old) =>
          old && Array.isArray(old.data)
            ? {
                ...old,
                data: old.data.filter((item) => item.analyticalVariableId !== analyticalVariableId),
              }
            : old
      );
      queryClient.removeQueries({
        queryKey: avKeys.libraryDetail(analyticalVariableId, projectId ?? ''),
      });
      if (projectId) {
        const key = projectArtifactsKey(projectId);
        queryClient.setQueryData<ListResponse<ProjectArtifactListEntryShape>>(key, (old) =>
          old && Array.isArray(old.data)
            ? { ...old, data: old.data.filter((entry) => entry.id !== analyticalVariableId) }
            : old
        );
        void queryClient.invalidateQueries({ queryKey: key });
      }
      void queryClient.invalidateQueries({ queryKey: avKeys.all });
    },
  });
};
